/**
 * #1124 report completeness: the Copy report's context lines (site, view,
 * transport, measurement boundary, liveness, source age). Pure formatting over
 * readings g1 already keeps. Unavailable is "—" with its reason, never 0, and
 * nothing here names a cause.
 */
import { computeFeedStatus } from '@/hooks/useFeedFreshness';

const DASH = '—';

/** The four deploy sites (AGENTS.md), named the way the team says them. */
const SITES: Record<string, string> = {
  'g1.gbuff.com': 'GBuff (production)',
  'g1.pm.newbx.com': 'PM (staging)',
  'dev.g1.pm.newbx.com': 'dev',
  'g1.dan.newbx.com': 'dan',
};

/** Exact host match only: anything else is unrecognized, never a known deploy. */
export function siteName(host: string): string {
  const name = host.replace(/:\d+$/, '');
  if (SITES[name]) return SITES[name];
  if (name === 'localhost' || name === '127.0.0.1' || name === '[::1]') return 'local dev';
  return 'unrecognized environment';
}

function servedText(build: string, served: string | null): string {
  if (served == null) return `served build ${DASH} (not checked yet)`;
  const commit = /^[0-9a-f]{7,40}$/i;
  if (!commit.test(served)) return `served build ${DASH} (unreadable identity)`;
  if (!commit.test(build)) return `served build ${served} (this tab's build is unknown)`;
  // Both version.json producers write `git rev-parse --short`, whose length grows when 7 hex chars are ambiguous.
  const same = served.startsWith(build) || build.startsWith(served);
  return `served build ${served} (${same ? 'this tab is current' : "differs from this tab's build"})`;
}

export function formatSiteLine(input: { host: string; build: string; served: string | null }): string {
  return `Site: ${siteName(input.host)} · host ${input.host || DASH} · build ${input.build} · ${servedText(input.build, input.served)}`;
}

/** The game a Game Detail card is looking at: the route, plus the row once it lands. */
export interface ReportGame {
  sport: string | null;
  away: string;
  home: string;
  date: string;
  gameNo: number | null;
  /** The resolved row's key; null before the row lands. */
  key: string | null;
  resolved: boolean;
}

export interface ReportView {
  page: string;
  game: ReportGame | null;
}

export function formatViewLine(view: ReportView): string {
  const g = view.game;
  if (g == null) return `View: ${view.page} · no single game`;
  const sport = g.sport ? `${g.sport.toUpperCase()} ` : '';
  if (!g.resolved) return `View: ${view.page} · from the address: ${sport}${g.away}/${g.home} · ${g.date} (no live row yet)`;
  const gameNo = g.gameNo != null ? ` · game ${g.gameNo}` : '';
  const key = g.key ? ` (key ${g.key})` : '';
  return `View: ${view.page} · ${sport}${g.away} @ ${g.home} · ${g.date}${gameNo}${key}`;
}

export interface TransportCounts {
  shared: number;
  direct: number;
  connecting: number;
  total: number;
}

export function formatTransportLine(t: TransportCounts): string {
  if (t.total === 0) return `Transport: ${DASH} (no feed connections yet)`;
  return `Transport: browser → hydra WebSockets · ${t.shared} through this browser's shared feed worker · ${t.direct} direct · ${t.connecting} connecting (${t.total} in all)`;
}

export const MEASUREMENT_LINE = 'Measured in this tab: counts are frames this tab parsed; sizes are decoded JSON characters, not network bytes. Nothing here measures hydra, the relay or another browser.';

function agoText(ms: number, now: number): string {
  return ms > 0 ? `${Math.max(0, Math.floor((now - ms) / 1000))} s ago` : `${DASH} (none yet)`;
}

export interface LivenessReading {
  /** The label of the one feed that drives the app's connected and keepalive signals. */
  feed: string;
  connected: boolean;
  lastDataMs: number;
  lastKeepaliveMs: number;
  now: number;
}

export function formatLivenessLine(i: LivenessReading): string {
  const label = `Feed liveness (status feed ${i.feed})`;
  if (!i.connected && i.lastDataMs === 0 && i.lastKeepaliveMs === 0) return `${label}: ${DASH} (not connected yet)`;
  const status = computeFeedStatus(i);
  return `${label}: ${status} · last data frame ${agoText(i.lastDataMs, i.now)} · last frame of any kind ${agoText(i.lastKeepaliveMs, i.now)}. `
    + 'This says the socket is alive, not that books are fresh.';
}

export interface SourceAgeReading {
  /** The view group whose books were read ("Game Detail"). */
  group: string;
  /** Books that group asked for. */
  books: number;
  /** Current source ages of the books that carry one, ms. */
  agesMs: readonly number[];
}

function seconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`;
}

function median(sorted: readonly number[]): number {
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function formatSourceAgeLine(r: SourceAgeReading | null): string {
  if (r == null) return `Source observation age: ${DASH} (not a single-game view)`;
  if (r.books === 0) return `Source observation age: ${DASH} (${r.group} asked for no books)`;
  const label = `Source observation age (${r.group}'s ${r.books.toLocaleString('en-US')} books)`;
  if (r.agesMs.length === 0) return `${label}: ${DASH} (none of them reported an age)`;
  const sorted = [...r.agesMs].sort((a, b) => a - b);
  return `${label}: oldest ${seconds(sorted[sorted.length - 1])} · median ${seconds(median(sorted))} · ${sorted.length} of ${r.books} report an age. `
    + "Hydra's observation age plus time held since; a quiet book ages without being late. Not venue event time, and separate from Tab behind.";
}

/** Decoded JSON characters per second (raw.length), compact: "1.2 M", "61.7 k", "512". */
export function formatCharsPerSec(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return DASH;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)} k`;
  return `${Math.round(n)}`;
}
